import { useCallback, useEffect, useState } from 'react';

// Hash history (the double-hash format) is on only when served from a `file:`
// URL — the same check router.ts makes. Dev Electron loads over http from Vite
// and uses browser history, so `inElectron()` would be the wrong test here.
const usesElectronHashRouter = () => window.location.protocol === 'file:';

// Helper functions to handle hash parsing and manipulation
const parseHashParts = (fullHash: string) => {
  const hashParts = fullHash.split('#');
  return {
    routePart: hashParts[1] || '', // e.g., '/chats' (TanStack Router route)
    customHash: hashParts[2] || '', // e.g., 'settings/General' (our custom hash)
    hasRoute: hashParts.length >= 2,
    hasCustomHash: hashParts.length >= 3,
  };
};

export const getHash = () => {
  const fullHash = window.location.hash;

  if (usesElectronHashRouter()) {
    // In Electron, TanStack Router creates hashes like: #/chats#settings/General
    // We need to extract the part after the second # (if it exists)
    // hashParts[0] = '' (empty string before first #)
    // hashParts[1] = '/chats' (TanStack Router route)
    // hashParts[2] = 'settings/General' (our custom hash)
    const { customHash } = parseHashParts(fullHash);
    return customHash;
  } else {
    // In web app, use normal hash parsing
    return fullHash.startsWith('#') ? fullHash.slice(1) : fullHash;
  }
};

export const setHashInElectron = (newHash: string) => {
  if (!usesElectronHashRouter()) {
    setHashInWeb(newHash);
    return;
  }

  const fullHash = window.location.hash;
  const { routePart, hasRoute } = parseHashParts(fullHash);

  if (hasRoute) {
    // Keep the route part, add our custom hash
    // Current: #/chats → Target: #/chats#settings/General
    window.location.hash = `#${routePart}#${newHash}`;
  } else {
    // Fallback if no route hash exists
    window.location.hash = `#/#${newHash}`;
  }
};

const setHashInWeb = (newHash: string) => {
  const hashValue = newHash.startsWith('#') ? newHash : `#${newHash}`;
  window.location.hash = hashValue;
};

const clearHashInElectron = () => {
  const fullHash = window.location.hash;
  const { routePart, hasRoute } = parseHashParts(fullHash);

  if (hasRoute) {
    // Keep only the route part
    window.location.hash = `#${routePart}`;
  } else {
    // Fallback to empty hash
    window.location.hash = '';
  }
};

const clearHashInWeb = () => {
  const url = new URL(window.location.href);
  url.hash = '';
  window.history.replaceState({}, '', url.toString());
};

export const clearHash = () => {
  if (usesElectronHashRouter()) {
    clearHashInElectron();
  } else {
    clearHashInWeb();
  }
  window.dispatchEvent(new HashChangeEvent('hashchange'));
};

export function useHashRoute() {
  const [hash, setHash] = useState<string>(getHash());

  useEffect(() => {
    const onHashChange = () => {
      const newHash = getHash();
      setHash(newHash);
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const updateHash = useCallback((newHash: string) => {
    if (usesElectronHashRouter()) {
      setHashInElectron(newHash);
    } else {
      setHashInWeb(newHash);
    }
  }, []);

  return { hash, setHash: updateHash, clearHash };
}
